/**
 * Saves a live scope's filters after the person changes them: a burst of
 * changes (ticking chips, dragging a range) is one save, `SAVE_DELAY_MS` after
 * the last. A listener on the filter actions, not a component effect.
 *
 * A failed save is not surfaced: the filters on screen are right either way,
 * and the next change saves again.
 */
import { isAnyOf } from '@reduxjs/toolkit';

import { listenerMiddleware } from '@/store/listener';
import {
  clearSelections,
  setCompare,
  setRange,
  setSelection,
  type FiltersSliceState,
} from '@/store/slices/filtersSlice';

import { filterStateApi } from './filterState.api';
import { refForKey } from './scope';

export const SAVE_DELAY_MS = 800;

let started = false;
/** The most recent change per scope — a save only goes out for the last of a burst. */
const latest = new Map<string, number>();

/** Register once; safe to call again. */
export function startFilterPersistence() {
  if (started) return;
  started = true;
  listenerMiddleware.startListening({
    matcher: isAnyOf(setRange, setCompare, setSelection, clearSelections),
    effect: async (action, listenerApi) => {
      // Every matched action carries the filter scope it changed.
      const { scope } = (action as unknown as { payload: { scope: string } }).payload;
      const ref = refForKey(scope);
      if (!ref) return;
      // The latest change of a scope wins; a pending save of another scope is kept.
      const turn = (latest.get(scope) ?? 0) + 1;
      latest.set(scope, turn);
      await listenerApi.delay(SAVE_DELAY_MS);
      if (latest.get(scope) !== turn) return;
      const filters = (listenerApi.getState() as { filters: FiltersSliceState }).filters[scope];
      if (!filters) return;
      await listenerApi.dispatch(
        filterStateApi.endpoints.saveFilters.initiate({ source: 'live', params: { ref, filters } }),
      );
    },
  });
}
